import { API_BASE_URL } from '@/config/api';
import { getJsonAuthHeaders } from '@/lib/apiHeaders';
import type { 
    ApiTokenInfo, 
    CreateApiTokenRequest, 
    CreateApiTokenResponse 
} from '@/types/WeeklyReport';

/**
 * Get auth headers for API requests (with CSRF protection)
 */
const getAuthHeaders = (): HeadersInit => getJsonAuthHeaders();

/**
 * API Token Service
 * 
 * Manages API tokens for programmatic access to the Weekly Report API.
 */
export const tokenService = {
    /**
     * Create a new API token
     */
    async createToken(request: CreateApiTokenRequest): Promise<CreateApiTokenResponse> {
        const response = await fetch(`${API_BASE_URL}/tokens`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(request)
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Failed to create token' }));
            throw new Error(error.error || 'Failed to create token');
        }
        
        return response.json();
    },

    /**
     * List all tokens for the current user
     */
    async listTokens(): Promise<ApiTokenInfo[]> {
        const response = await fetch(`${API_BASE_URL}/tokens`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Failed to list tokens' }));
            throw new Error(error.error || 'Failed to list tokens');
        }
        
        const data = await response.json();
        return data.tokens;
    },

    /**
     * Revoke an API token
     */
    async revokeToken(tokenId: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/tokens/${tokenId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Failed to revoke token' }));
            throw new Error(error.error || 'Failed to revoke token');
        }
    }
};

